"use client";

import { useCallback, useState } from "react";
import {
  authenticateWithPassword,
  type UsersLoginRequest,
  type UsersLoginSuccess,
  type UsersLoginOptions,
  UsersLoginError,
} from "./client";

export type UseUsersLoginOptions = Pick<UsersLoginOptions, "endpoint" | "fetcher" | "signal">;

export interface UseUsersLoginResult {
  readonly login: (credentials: UsersLoginRequest) => Promise<UsersLoginSuccess | null>;
  readonly isLoading: boolean;
  readonly error: string | null;
  readonly result: UsersLoginSuccess | null;
  readonly reset: () => void;
}

export function useUsersLogin(options: UseUsersLoginOptions = {}): UseUsersLoginResult {
  const { endpoint, fetcher, signal } = options;
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<UsersLoginSuccess | null>(null);

  const login = useCallback(
    async (credentials: UsersLoginRequest) => {
      setLoading(true);
      setError(null);
      setResult(null);

      try {
        const response = await authenticateWithPassword(credentials, { endpoint, fetcher, signal });
        setResult(response);
        return response;
      } catch (err) {
        let message = "Unexpected error";
        if (err instanceof UsersLoginError) {
          message = err.message;
        } else if (err instanceof Error) {
          message = err.message;
        }
        setError(message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [endpoint, fetcher, signal],
  );

  const reset = useCallback(() => {
    setError(null);
    setResult(null);
  }, []);

  return { login, isLoading, error, result, reset };
}

export type { UsersLoginRequest, UsersLoginSuccess } from "./client";
